import {Component, OnDestroy, OnInit} from '@angular/core';
import {NgxSpinnerService} from 'ngx-spinner';
import {Subscription} from 'rxjs/Subscription';
import {Api, isPlanQuotaRejection, normalizeClaimCode} from '../../../providers/api';
import {Auth} from '../../../providers/auth';
import {EntitlementService, isAtCap} from '../../../providers/entitlement.service';
import {Grupo} from '../../../providers/models';

// Slice-2b upgrade copy (matches Auth.checkError plan_limit_screens
// wording): used both for the at-cap new-equipo block and for every
// server-side quota rejection, so users always see the same upgrade path.
export const QUOTA_UPGRADE_MESSAGE = 'Has alcanzado el límite de pantallas de tu plan. Libera una pantalla o mejora tu plan.';

@Component({
  selector: 'app-vincular-tv',
  templateUrl: './vincular-tv.component.html',
  styleUrls: ['./vincular-tv.component.css']
})
export class VincularTvComponent implements OnInit, OnDestroy {

  circuito = {
    id: 0, guid: '', nombre: ''
  };

  grupos: Grupo[] = [];
  equipos: any[] = [];

  claimCode = '';
  useNewEquipo = false;
  selectedGrupo: any = null;
  selectedEquipo: any = null;
  newEquipoName = '';

  approving = false;
  approved = false;
  errorMessage: string = null;
  showPagoCta = false;

  // Plan quota snapshot from the shared entitlement store (fed by
  // getTrialStatus, which already carries maxScreens/screensInUse — no new
  // endpoint). Null pair = unknown = fail-open (legacy backend shapes).
  screensInUse: number = null;
  maxScreens: number = null;
  quotaAtCap = false;
  private quotaSub: Subscription = null;

  constructor(private spinner: NgxSpinnerService,
              private api: Api, private auth: Auth,
              private entitlement: EntitlementService) {
    this.spinner.hide();
    this.quotaSub = this.entitlement.entitlement$.subscribe((e) => {
      this.screensInUse = e ? e.screensInUse : null;
      this.maxScreens = e ? e.maxScreens : null;
      this.quotaAtCap = isAtCap(e);
    });
    this.auth.userCheck(() => {
      this.circuito = this.auth.circuito;
      this.loadTargets();
      this.loadQuota();
    }, (err) => {
      this.spinner.hide();
      this.auth.logout();
    });
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    if (this.quotaSub) {
      this.quotaSub.unsubscribe();
      this.quotaSub = null;
    }
  }

  // Grupo/equipo selector data source is GET /api/admin/getSettings.
  // NEVER Admin.programming(): it indexes res[0] unchecked.
  loadTargets() {
    this.spinner.show();
    this.api.getSettings().subscribe((res: any) => {
      const data = (res && res.data) ? res.data : {};
      this.grupos = data.grupos || [];
      this.equipos = data.pos || [];
      this.spinner.hide();
    }, err => {
      console.log(err);
      this.spinner.hide();
      this.setError(this.auth.checkError(err), 'No se pudieron cargar grupos y equipos', err);
    });
  }

  // Freshness feed for the quota snapshot: same getTrialStatus payload the
  // setting screen uses (maxScreens/screensInUse via setFromStatus). The
  // shared store may already hold AppComponent's copy — this just refreshes
  // it. Fail-open: on error the store (and snapshot) keep their values.
  loadQuota() {
    if (!this.circuito || !this.circuito.guid) {
      return;
    }
    this.api.getTrialStatus(this.circuito.guid).subscribe((res: any) => {
      const data = res && res.data ? res.data : res;
      this.entitlement.setFromStatus(data);
    }, err => {
      console.log(err);
    });
  }

  selectGrupo(grupo) {
    this.selectedGrupo = grupo;
  }

  selectEquipo(equipo) {
    this.selectedEquipo = equipo;
  }

  grupoName(equipo): string {
    if (!equipo || !this.grupos) {
      return '';
    }
    for (let i = 0; i < this.grupos.length; i++) {
      if (this.grupos[i].id === equipo.idGrupo) {
        return this.grupos[i].nombre as string;
      }
    }
    return '';
  }

  canApprove(): boolean {
    if (this.approving) {
      return false;
    }
    if (!normalizeClaimCode(this.claimCode)) {
      return false;
    }
    // Quota gate is new-equipo-only server-side (equipoId rebind skips it),
    // so at cap only the new-equipo path is blocked — rebind stays allowed.
    if (this.useNewEquipo && this.quotaAtCap) {
      return false;
    }
    if (this.useNewEquipo) {
      return !!((this.newEquipoName && this.newEquipoName.trim()) && this.selectedGrupo);
    }
    return !!this.selectedEquipo;
  }

  approve() {
    this.errorMessage = null;
    this.showPagoCta = false;
    // Codes are case-sensitive: trim surrounding whitespace only.
    const code = normalizeClaimCode(this.claimCode);
    if (!code) {
      this.errorMessage = 'Ingresá el código que muestra el TV';
      return;
    }
    // Defense in depth for the at-cap block (canApprove already disables
    // the button): never emit a quota-gated approve from this screen.
    if (this.useNewEquipo && this.quotaAtCap) {
      this.setError({message: QUOTA_UPGRADE_MESSAGE, cta: '/pago', code: 'plan_limit_screens'}, QUOTA_UPGRADE_MESSAGE, null);
      return;
    }
    const body: any = {code: code};
    if (this.useNewEquipo) {
      const name = (this.newEquipoName || '').trim();
      if (!name || !this.selectedGrupo) {
        this.errorMessage = 'Elegí un grupo y un nombre para el equipo nuevo';
        return;
      }
      // New-equipo creation IS quota-gated server-side.
      body.newEquipo = {name: name, grupoId: this.selectedGrupo.id};
    } else {
      if (!this.selectedEquipo) {
        this.errorMessage = 'Elegí el equipo al que querés vincular el TV';
        return;
      }
      // equipoId rebind intentionally skips the quota gate server-side
      // (paywall still enforced).
      body.equipoId = this.selectedEquipo.id;
    }
    this.approving = true;
    this.spinner.show();
    this.api.approveClaim(body).subscribe((res: any) => {
      console.log(res);
      this.approving = false;
      this.approved = true;
      this.spinner.hide();
    }, err => {
      console.log(err);
      this.approving = false;
      this.approved = false;
      this.spinner.hide();
      this.setError(this.auth.checkError(err), 'No se pudo vincular el TV', err);
    });
  }

  setError(info: any, fallback: string, rawErr?: any) {
    this.errorMessage = (info && info.message) ? info.message : fallback;
    this.showPagoCta = !!(info && info.cta === '/pago');
    // The real approve-claim quota rejection ships the backend envelope raw
    // ({code:403, error:'plan_limit_screens'}), which the central mapper
    // does not recognize — always unify it to the same upgrade copy, never
    // a raw server message.
    if (rawErr && isPlanQuotaRejection(rawErr)) {
      this.errorMessage = QUOTA_UPGRADE_MESSAGE;
      this.showPagoCta = true;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/setting/vincular-tv/vincular-tv.component.ts